import { Position } from '@turf/turf';
import {
  CELL_EMPTY,
  CELL_FILLED,
  CoverageGrid,
  NO_INDEX,
  cellsWhere,
  fillGridPolygonInterior,
  isFilledCell,
} from 'app/utils/field-boundary/grid';
import {
  CHAMFER_ORTHOGONAL_STEP,
  propagateChamfer,
  seedDistances,
} from 'app/utils/field-boundary/morphology';
import {
  MIN_RING_VERTICES,
  isPositiveNumber,
} from 'app/utils/field-boundary/shared';

/**
 * Blocks smaller than this share of the largest one are scraps (a test run at
 * the gate, a leftover stub) and must not stretch the boundary towards them.
 */
const MIN_JOINED_BLOCK_FRACTION = 0.05;

interface ComponentLabels {
  /** Component of each cell, or `NO_INDEX` for the cells left out. */
  labels: Int32Array;
  /** Cell count of each component. */
  sizes: number[];
}

interface ComponentGap {
  first: number;
  second: number;
  /** In chamfer steps. */
  distance: number;
}

interface BlockGroups {
  /** Group of each component, named after one of its members. */
  groupOf: number[];
  /** Cell count of each group, indexed by its name. */
  groupSizes: number[];
}

/** Labels the 4-connected components of the filled cells in `cells`. */
function labelComponents(
  grid: CoverageGrid,
  cells: Uint8Array,
): ComponentLabels {
  const labels = new Int32Array(cells.length).fill(NO_INDEX);
  const queue = new Int32Array(cells.length);
  const sizes: number[] = [];

  for (let start = 0; start < cells.length; start++) {
    if (cells[start] === CELL_FILLED && labels[start] === NO_INDEX) {
      const label = sizes.length;
      let head = 0;
      let tail = 0;
      queue[tail++] = start;
      labels[start] = label;
      const visit = (index: number) => {
        if (cells[index] === CELL_FILLED && labels[index] === NO_INDEX) {
          labels[index] = label;
          queue[tail++] = index;
        }
      };
      while (head < tail) {
        const index = queue[head++];
        const column = index % grid.columns;
        const row = Math.floor(index / grid.columns);
        if (column > 0) {
          visit(index - 1);
        }
        if (column < grid.columns - 1) {
          visit(index + 1);
        }
        if (row > 0) {
          visit(index - grid.columns);
        }
        if (row < grid.rows - 1) {
          visit(index + grid.columns);
        }
      }
      sizes.push(tail);
    }
  }
  return { labels, sizes };
}

/** Drops disconnected leftovers, such as short stretches worked away from the field. */
export function keepLargestComponent(grid: CoverageGrid): void {
  const { labels, sizes } = labelComponents(grid, grid.cells);
  let largest = NO_INDEX;
  sizes.forEach((size, component) => {
    if (largest === NO_INDEX || size > sizes[largest]) {
      largest = component;
    }
  });
  grid.cells = cellsWhere(
    grid,
    (index) => largest !== NO_INDEX && labels[index] === largest,
  );
}

/**
 * Fills every empty area not reachable from the grid border.
 *
 * @remarks
 * Together with `keepLargestComponent`, this leaves a single 4-connected area
 * whose complement is also 4-connected, which rules out cells touching only
 * by a corner — so `traceOuterRing` always finds one simple ring.
 */
export function fillHoles(grid: CoverageGrid): void {
  const empty = cellsWhere(grid, (index) => grid.cells[index] === CELL_EMPTY);
  const { labels } = labelComponents(grid, empty);
  const outside = new Set<number>();
  const markOutside = (index: number) => {
    if (labels[index] !== NO_INDEX) {
      outside.add(labels[index]);
    }
  };
  for (let column = 0; column < grid.columns; column++) {
    markOutside(column);
    markOutside((grid.rows - 1) * grid.columns + column);
  }
  for (let row = 0; row < grid.rows; row++) {
    markOutside(row * grid.columns);
    markOutside(row * grid.columns + grid.columns - 1);
  }
  grid.cells = cellsWhere(
    grid,
    (index) => grid.cells[index] === CELL_FILLED || !outside.has(labels[index]),
  );
}

/**
 * Shortest distance between every pair of neighboring components, measured
 * across the frontier where their nearest-component regions meet.
 */
function componentGaps(
  grid: CoverageGrid,
  labels: Int32Array,
  componentCount: number,
): ComponentGap[] {
  const distances = seedDistances(grid, (index) => labels[index] !== NO_INDEX);
  const nearest = Int32Array.from(labels);
  propagateChamfer(grid, distances, nearest);

  const shortest = new Map<number, number>();
  const record = (index: number, neighbor: number) => {
    const first = Math.min(nearest[index], nearest[neighbor]);
    const second = Math.max(nearest[index], nearest[neighbor]);
    if (first !== second && first !== NO_INDEX) {
      const key = first * componentCount + second;
      const distance =
        distances[index] + distances[neighbor] + CHAMFER_ORTHOGONAL_STEP;
      const known = shortest.get(key);
      if (known === undefined || distance < known) {
        shortest.set(key, distance);
      }
    }
  };
  for (let row = 0; row < grid.rows; row++) {
    for (let column = 0; column < grid.columns; column++) {
      const index = row * grid.columns + column;
      if (column < grid.columns - 1) {
        record(index, index + 1);
      }
      if (row < grid.rows - 1) {
        record(index, index + grid.columns);
      }
    }
  }

  const gaps: ComponentGap[] = [];
  shortest.forEach((distance, key) => {
    gaps.push({
      first: Math.floor(key / componentCount),
      second: key % componentCount,
      distance,
    });
  });
  return gaps;
}

/** Cross product of (a - origin) x (b - origin); positive for a left turn. */
function crossProduct(origin: Position, a: Position, b: Position): number {
  return (
    (a[0] - origin[0]) * (b[1] - origin[1]) -
    (a[1] - origin[1]) * (b[0] - origin[0])
  );
}

/** Convex hull (Andrew's monotone chain), counterclockwise and open. */
function convexHull(points: Position[]): Position[] {
  const sorted = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const buildChain = (ordered: Position[]): Position[] => {
    const chain: Position[] = [];
    ordered.forEach((candidate) => {
      while (
        chain.length >= 2 &&
        crossProduct(
          chain[chain.length - 2],
          chain[chain.length - 1],
          candidate,
        ) <= 0
      ) {
        chain.pop();
      }
      chain.push(candidate);
    });
    chain.pop();
    return chain;
  };
  return [...buildChain(sorted), ...buildChain([...sorted].reverse())];
}

/**
 * Fills the convex hull of the filled cells. Only the corners of the cells on
 * the outline can be hull vertices, so the inner ones are skipped.
 *
 * @remarks
 * Only cells whose center is inside the hull are filled. The hull runs along
 * cell sides, so marking the cells its edges touch, as the trails are
 * rasterized, would grow the joined area by one cell to the north and east.
 */
function fillConvexHull(grid: CoverageGrid): void {
  const corners: Position[] = [];
  for (let row = 0; row < grid.rows; row++) {
    for (let column = 0; column < grid.columns; column++) {
      const isOutline =
        isFilledCell(grid, column, row) &&
        (!isFilledCell(grid, column - 1, row) ||
          !isFilledCell(grid, column + 1, row) ||
          !isFilledCell(grid, column, row - 1) ||
          !isFilledCell(grid, column, row + 1));
      if (isOutline) {
        corners.push(
          [column, row],
          [column + 1, row],
          [column, row + 1],
          [column + 1, row + 1],
        );
      }
    }
  }
  const hull = convexHull(corners);
  if (hull.length >= MIN_RING_VERTICES) {
    fillGridPolygonInterior(
      grid,
      hull.map(([x]) => x),
      hull.map(([, y]) => y),
    );
  }
}

/**
 * Groups the components whose gap is at most `maxJoinGapMeters` (single
 * linkage, so a chain of close blocks forms one group). Components labeled
 * `NO_INDEX` take no part and stay in groups of their own.
 */
function groupNearbyBlocks(
  grid: CoverageGrid,
  labels: Int32Array,
  sizes: number[],
  maxJoinGapMeters: number,
): BlockGroups {
  const parents = sizes.map((_, component) => component);
  const groupSizes = [...sizes];
  const findGroup = (component: number): number => {
    let root = component;
    while (parents[root] !== root) {
      parents[root] = parents[parents[root]];
      root = parents[root];
    }
    return root;
  };

  const maxGap =
    (maxJoinGapMeters / grid.cellSizeMeters) * CHAMFER_ORTHOGONAL_STEP;
  componentGaps(grid, labels, sizes.length)
    .filter((gap) => gap.distance <= maxGap)
    .sort((a, b) => a.distance - b.distance)
    .forEach((gap) => {
      const first = findGroup(gap.first);
      const second = findGroup(gap.second);
      if (first !== second) {
        parents[second] = first;
        groupSizes[first] += groupSizes[second];
      }
    });

  return {
    groupOf: sizes.map((_, component) => findGroup(component)),
    groupSizes,
  };
}

function largestGroup({ groupOf, groupSizes }: BlockGroups): number {
  let chosen = NO_INDEX;
  groupOf.forEach((group, component) => {
    const isGroupName = group === component;
    if (
      isGroupName &&
      (chosen === NO_INDEX || groupSizes[component] > groupSizes[chosen])
    ) {
      chosen = component;
    }
  });
  return chosen;
}

/**
 * Joins the worked blocks that belong to the same field and drops the rest.
 *
 * @remarks
 * Blocks are grouped when the gap between them is at most `maxJoinGapMeters`,
 * scraps are ignored, and the group with the largest worked area wins.
 *
 * What lies between the blocks was not worked, so any boundary there is a
 * guess: the convex hull of the group is the simplest one, and it matches a
 * field worked in strips, whose unworked strips end at the same edges as the
 * worked ones. It also swallows wide bends of the field edge between blocks,
 * which the user corrects on the map. A single block keeps its own outline.
 */
export function joinNearbyBlocks(
  grid: CoverageGrid,
  maxJoinGapMeters: number,
): void {
  const { labels, sizes } = labelComponents(grid, grid.cells);
  const largestSize = sizes.reduce(
    (largest, size) => Math.max(largest, size),
    0,
  );
  const minBlockSize = largestSize * MIN_JOINED_BLOCK_FRACTION;
  const blockCount = sizes.filter((size) => size >= minBlockSize).length;

  if (!isPositiveNumber(maxJoinGapMeters) || blockCount <= 1) {
    keepLargestComponent(grid);
    return;
  }

  // Scraps leave the gap measurement, so they can neither join a group nor
  // pull it towards them. Their groups can never outweigh the largest block.
  for (let index = 0; index < labels.length; index++) {
    if (labels[index] !== NO_INDEX && sizes[labels[index]] < minBlockSize) {
      labels[index] = NO_INDEX;
    }
  }

  const groups = groupNearbyBlocks(grid, labels, sizes, maxJoinGapMeters);
  const chosenGroup = largestGroup(groups);
  grid.cells = cellsWhere(
    grid,
    (index) =>
      labels[index] !== NO_INDEX &&
      groups.groupOf[labels[index]] === chosenGroup,
  );
  const memberCount = groups.groupOf.filter(
    (group) => group === chosenGroup,
  ).length;
  if (memberCount > 1) {
    fillConvexHull(grid);
  }
  keepLargestComponent(grid);
}
